import { sendAnalyticsMessage } from '../../../sdk/utils/sdk-messenger'
import { AnalyticsServiceSettings } from 'models/AnalyticsServiceSettings'

// Define a flexible type for properties to allow custom fields for identify/group events
type FlexibleProperties = Record<string, unknown>

interface EventItem {
  currency: string
  item_id: string
  item_name: string
  item_category: string
}

interface AnalyticsProperties {
  label?: string
  action?: string
  currency?: string
  items?: EventItem[]
}

interface AnalyticsMeta {
  rid: string
  ts: number
  hasCallback: boolean
}

interface AnalyticsPayload {
  type: string
  event?: string // Make 'event' optional to support identify/group events
  properties: AnalyticsProperties | FlexibleProperties // Allow flexible properties for identify/group
  options?: Record<string, unknown>
  userId?: string
  anonymousId?: string
  meta: AnalyticsMeta
}

interface FullAnalyticsPayload {
  payload: AnalyticsPayload
}

interface MakeCrossFrameAnalyticsPluginProps {
  settings: AnalyticsServiceSettings[]
}

export const makeCrossFrameAnalyticsPlugin = ({}: MakeCrossFrameAnalyticsPluginProps) => {
  return {
    name: 'cross-frame-analytics',
    config: {},

    initialize(): void {
      // No initialization needed
    },

    /**
     * Handle 'track' events (e.g., custom events like 'begin_checkout', 'button_click')
     */
    track: (event: FullAnalyticsPayload): void => {
      const { event: eventName, properties } = event.payload

      sendAnalyticsMessage(eventName || '', properties || {})
    },

    /**
     * Handle 'page' events — currently not used for cross-frame analytics
     */
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    page: ({ event, properties }: AnalyticsPayload): void => {
      // Placeholder for page view event handling if needed
    },

    /**
     * Handle 'identify' events (e.g., identifying a user)
     */
    identify: (event: FullAnalyticsPayload): void => {
      const { userId, anonymousId, properties } = event.payload

      const identifyData = {
        userId: userId || 'anonymous',
        anonymousId,
        ...properties,
      }

      sendAnalyticsMessage('identify', identifyData)
    },

    /**
     * Handle 'group' events (e.g., associating a user with a group)
     */
    group: (event: FullAnalyticsPayload): void => {
      const { userId, anonymousId, properties } = event.payload

      const groupData = {
        userId: userId || 'anonymous',
        anonymousId,
        ...properties,
      }

      sendAnalyticsMessage('group', groupData)
    },

    /**
     * Check if the plugin is loaded
     */
    loaded: () => true,
  }
}

export default makeCrossFrameAnalyticsPlugin
